import { isNativeApp } from './native';
import { registerNativeDevice } from './pushSession';
import { ensureDispatchAlertChannel, playDispatchAlertSound } from './dispatchAlertSound';

let pushListenersBound = false;

/** Register Capacitor native push (FCM/APNs) for store apps. */
export async function initNativePush(token: string, resourceId: string): Promise<void> {
  if (!isNativeApp()) return;

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const { Capacitor } = await import('@capacitor/core');

    if (Capacitor.getPlatform() === 'web') return;

    const perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt') {
      await PushNotifications.requestPermissions();
    }

    if (!pushListenersBound) {
      pushListenersBound = true;

      await ensureDispatchAlertChannel();

      await PushNotifications.addListener('registration', async (ev) => {
        if (ev.value && token) {
          await registerNativeDevice(token, ev.value, resourceId);
        }
      });

      await PushNotifications.addListener('registrationError', (err) => {
        console.error('Native push registration error:', err);
      });

      await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        const data = notification.data || {};
        if (data.type === 'dispatch' || data.dispatchId) {
          void playDispatchAlertSound();
        }
      });

      await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        const data = action.notification.data || {};
        if (data.type === 'dispatch' || data.dispatchId) {
          window.location.href = '/?dispatch=1';
        } else if (data.type === 'chat') {
          window.location.href = '/?chat=1';
        }
      });
    }

    await PushNotifications.register();
  } catch (err) {
    console.error('initNativePush failed:', err);
  }
}

/** Deep link handler — notification tap when app opens from cold start. */
export async function initNativeAppLinks(): Promise<void> {
  if (!isNativeApp()) return;

  try {
    const { App } = await import('@capacitor/app');

    App.addListener('appUrlOpen', (event) => {
      try {
        const url = new URL(event.url);
        const path = url.pathname + url.search;
        if (path && path !== '/') {
          window.location.href = path;
        }
      } catch { /* ignore */ }
    });

    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        window.dispatchEvent(new CustomEvent('mdt:app-resumed'));
      }
    });
  } catch { /* ignore */ }
}

/** Status bar + splash for native shell. */
export async function initNativeChrome(): Promise<void> {
  if (!isNativeApp()) return;

  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#0f172a' });
    await SplashScreen.hide();
  } catch { /* ignore */ }
}
